import type {
  BabyQuote,
  CatQuote,
  ChildQuote,
  DogQuote,
  HouseholderQuote,
  PolicyHolder,
  Quote,
  TravelQuote,
} from "@/api_client";
import { matchProductType } from "@/utils/productUtils";

import type { ObjectId, ObjectInfo } from "../types/types";

const getObjectInfo = (
  enigmatizePolicyHolder: PolicyHolder | undefined | null,
  product: Quote,
  enigmatizedProduct: Quote | undefined,
): ObjectInfo => {
  const householderQuote = product as HouseholderQuote;
  return {
    objectId: getProductSpecificObjectId(
      enigmatizePolicyHolder,
      product,
      enigmatizedProduct,
    ),
    objectSize: {
      value: householderQuote?.householder?.livingSpace,
      unit: "m2",
    },
    objectValue: getProductSpecificObjectValue(product) ?? undefined,
    postalCode: householderQuote?.householder?.postalCode ?? "",
    year: undefined,
    leased: undefined,
    numberOfBuildings: undefined,
    brand: undefined,
  };
};

const getProductSpecificObjectId = (
  enigmatizedPolicyHolder: PolicyHolder | undefined | null,
  product: Quote,
  enigmatizedProduct: Quote | undefined,
) => {
  const quoteForMatch = enigmatizedProduct || product;

  return matchProductType<ObjectId>(quoteForMatch, {
    householder: () => getHouseholderObjectId(product),
    baby: (enigmatizedBabyQuote) =>
      getBabyObjectId(enigmatizedPolicyHolder, enigmatizedBabyQuote),
    travel: (enigmatizedTravelQuote) =>
      getTravelObjectId(enigmatizedPolicyHolder, enigmatizedTravelQuote),
    car: {
      id: undefined,
      type: "",
    },
    income: {
      id: undefined,
      type: "",
    },
    sport: {
      id: undefined,
      type: "",
    },
    van: {
      id: undefined,
      type: "",
    },
    child: (enigmatizedChildQuote) => getChildObjectId(enigmatizedChildQuote),
    cat: (enigmatizedCatQuote) => getCatObjectId(enigmatizedCatQuote),
    dog: (enigmatizedDogQuote) => getDogObjectId(enigmatizedDogQuote),
    "": {
      id: undefined,
      type: "",
    },
  });
};

const getProductSpecificObjectValue = (product: Quote) =>
  matchProductType(product, {
    householder: undefined,
    baby: undefined,
    travel: undefined,
    car: undefined,
    income: undefined,
    sport: undefined,
    child: undefined,
    cat: (catQuote) => catQuote?.catCovered?.compensationLimit,
    dog: (dogQuote) => dogQuote?.dogCovered?.compensationLimit,
    van: undefined,
    "": undefined,
  });

const getBabyObjectId = (
  enigmatizePolicyHolder: PolicyHolder | undefined | null,
  enigmatizedProduct: BabyQuote | undefined,
) => {
  const id =
    enigmatizedProduct?.babyMother?.ssn ?? enigmatizePolicyHolder?.ssn ?? "";
  return { type: "SSN", id };
};

const getTravelObjectId = (
  enigmatizePolicyHolder: PolicyHolder | undefined | null,
  enigmatizedProduct: TravelQuote | undefined,
) => {
  const id =
    enigmatizedProduct?.coveredPerson?.familyMember?.ssn ??
    enigmatizePolicyHolder?.ssn ??
    "";
  return { type: "SSN", id };
};

const getHouseholderObjectId = (product: Quote) => {
  const householderQuote = product as HouseholderQuote;
  return {
    type: "OTH",
    id: householderQuote.householder?.livingSpace?.toString() ?? "",
  };
};

const getChildObjectId = (enigmatizedProduct?: ChildQuote) => ({
  type: "SSN",
  id: enigmatizedProduct?.coveredPerson?.ssn ?? "",
});

const getCatObjectId = (enigmatizedProduct?: CatQuote) => ({
  type: "ANAM",
  id: enigmatizedProduct?.catCovered?.catName ?? "",
});

const getDogObjectId = (enigmatizedProduct?: DogQuote) => ({
  type: "ANAM",
  id: enigmatizedProduct?.dogCovered?.dogName ?? "",
});

export default {
  getObjectInfo,
};
